import React, {
  useCallback,
  useContext,
  useState,
  useRef,
  useLayoutEffect,
} from "react";
import { ChatPrivilege } from "@zoom/videosdk";
import ChatMessageItem from "./component/chat-message-item";
import "./chat.scss";
import { ZoomContext } from "../zoom-context";
import CustomTextArea from "../../components/CustomTextArea";
import Flex from "../../components/Flex";
import ChatReceiverContainer from "./component/chat-receiver";
import { Button, Grid } from "@mui/material";
import { ChatReceiver, ChatRecord } from "./chat-types";
import EmojiPopover from "./component/EmojiPopover";
import Text from "../../components/Text";

interface Props {
  records: ChatRecord[];
  receivers: ChatReceiver[];
  privilege: ChatPrivilege;
  chatUser: ChatReceiver | null;
  isHostOrManager: boolean;
  setChatUserId: (userId: number) => void;
  sendMessage: (text: string) => void;
  sendFile: (file: File) => Promise<void>;
  resendFile: (retryToken: string, fileUuid: string) => Promise<void>;
  downloadFile: (id: string, blob?: boolean) => Promise<void>;
}

const ChatContainer = ({
  records,
  privilege,
  chatUser,
  isHostOrManager,
  setChatUserId,
  sendMessage,
  sendFile,
  resendFile,
  downloadFile,
}: Props) => {
  const zmClient = useContext(ZoomContext);
  if (!zmClient) {
    throw new Error("ZoomContext is not available. ");
  }
  const [chatDraft, setChatDraft] = useState<string>("");

  const chatWrapRef = useRef<HTMLDivElement | null>(null);
  const isEnterKeyPress = useRef<boolean>(false);

  const onChatInput = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (isEnterKeyPress.current) return;

      setChatDraft(event.target.value);
    },
    []
  );

  const onChatPressEnter = useCallback(() => {
    if (chatUser && chatDraft && chatDraft?.trim() !== "") {
      sendMessage(chatDraft);
      setChatDraft("");
    }
  }, [sendMessage, chatUser, chatDraft]);

  useLayoutEffect(() => {
    if (chatWrapRef.current) {
      chatWrapRef.current.scrollTo(0, chatWrapRef.current.scrollHeight);
    }
  }, [records]);

  return (
    <div className="chat-container">
      <div className="chat-wrap">
        <Flex
          width={"100%"}
          alignItems={"center"}
          justifyContent={"center"}
          pr={1}
        >
          <Flex
            border={"solid 0.5px #1A1A1A33"}
            bgcolor={"#D0D0D033"}
            borderRadius={2}
            width={"470px"}
            height={"60px"}
            alignItems={"center"}
            justifyContent={"center"}
          >
            <Text align="center">
              Messages can be seen only people in the call and are deleted when
              the call ends
            </Text>
          </Flex>
        </Flex>
        <div className="chat-message-wrap" ref={chatWrapRef}>
          {records.map((record) => (
            <ChatMessageItem
              record={record}
              currentUserId={zmClient.getSessionInfo().userId}
              setChatUser={setChatUserId}
              key={record.timestamp}
              resendFile={resendFile}
              downloadFile={downloadFile}
            />
          ))}
        </div>
        {/* <div className="chat-message-box"> */}
        {ChatPrivilege.NoOne !== privilege || isHostOrManager ? (
          <Grid container alignItems={"center"}>
            <Grid item xs={1.8}>
              <ChatReceiverContainer sendFile={sendFile} />
            </Grid>
            <Grid item xs={7}>
              <CustomTextArea
                sx={{
                  borderRadius: "40px",
                  color: "#004186",
                }}
                onKeyDown={(e) => {
                  isEnterKeyPress.current = e.key === "Enter";
                  e.key === "Enter" && !e.shiftKey && onChatPressEnter();
                }}
                fullWidth
                onChange={onChatInput}
                value={chatDraft}
                placeholder="Type your message"
              />
            </Grid>
            <Grid item xs={1}>
              <EmojiPopover value={chatDraft} onChange={setChatDraft} />
            </Grid>
            <Grid item xs={2}>
              <Button onClick={() => onChatPressEnter()}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="48"
                  height="48"
                  viewBox="0 0 48 48"
                >
                  <g
                    id="Group_93491"
                    data-name="Group 93491"
                    transform="translate(-1330.083 -1000)"
                  >
                    <circle
                      id="Ellipse_1320"
                      data-name="Ellipse 1320"
                      cx="24"
                      cy="24"
                      r="24"
                      transform="translate(1330.083 1000)"
                      fill="#004186"
                    />
                    <g
                      id="noun-send-1076084"
                      transform="translate(1351.081 1007.213) rotate(45)"
                    >
                      <path
                        id="Path_18375"
                        data-name="Path 18375"
                        d="M.61,10.581l9.049,3.5,3.5,9.049a.907.907,0,0,0,.9.61.967.967,0,0,0,.9-.61L23.68,1.308A.941.941,0,0,0,23.455.281,1,1,0,0,0,22.428.057L.609,8.784a.967.967,0,0,0-.609.9.95.95,0,0,0,.61.9ZM21.049,2.688,14.086,20.143l-2.439-6.321,3.53-3.53A.953.953,0,0,0,13.83,8.945l-3.4,3.337L3.594,9.651Z"
                        fill="#fff"
                      />
                    </g>
                  </g>
                </svg>
              </Button>
            </Grid>
          </Grid>
        ) : (
          <div className="chat-disabled">Chat disabled</div>
        )}
      </div>
    </div>
  );
};

export default ChatContainer;
